import { fetchJSON as fetchAPI } from './client';

export type RevenueOpsDashboard = {
  tenant_id: string;
  total_campaigns: number;
  active_campaigns: number;
  total_spend: number;
  total_sales: number;
  blended_roas: number;
  blended_acos: number;
  tacos: number;
  organic_revenue_pct: number;
  revenue_gap_pct: number;
  active_recommendations: number;
  open_opportunities: number;
  generated_at?: string;
};

export type CampaignRecord = {
  id: string;
  campaign_id?: string;
  name: string;
  type: string;
  status: string;
  portfolio?: string;
  daily_budget: number;
  spend: number;
  sales: number;
  orders: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpc: number;
  roas: number;
  acos: number;
  tacos: number;
  ntb_orders: number;
  ntb_percent: number;
  bidding_rule?: string;
};

export type KeywordRecord = {
  id: string;
  campaign_id?: string;
  keyword: string;
  match_type?: string;
  bid: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpc: number;
  orders: number;
  sales: number;
  acos: number;
  is_negative: boolean;
};

export type SearchTermRecord = {
  id: string;
  campaign_id?: string;
  search_term: string;
  impressions: number;
  clicks: number;
  orders: number;
  sales: number;
  acos: number;
  organic_rank?: number;
};

export type BudgetRecord = {
  id: string;
  portfolio?: string;
  period?: string;
  allocated_budget: number;
  actual_spend: number;
  utilization_pct: number;
  recommended_adjustment: number;
  rule?: string;
};

export type RevenueForecastRecord = {
  id: string;
  forecast_date: string;
  period?: string;
  revenue: number;
  orders: number;
  ad_spend: number;
  profit: number;
  roas: number;
  acos: number;
  organic_lift_pct: number;
  customer_growth_pct: number;
  status: string;
};

export type RevenuePlanRecord = {
  id: string;
  period: string;
  goal: string;
  target_revenue: number;
  actual_revenue: number;
  gap_pct: number;
  ad_budget: number;
  organic_target: number;
  paid_target: number;
  roadmap?: string;
  status: string;
};

export type OpportunityRecord = {
  id: string;
  category: string;
  title: string;
  description?: string;
  sku?: string;
  impact?: string;
  priority?: string;
  status: string;
};

export type RevenueRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  campaign_id?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

const API_BASE = import.meta.env.VITE_REVENUEOPS_API ?? '/api/v1/revenueops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const revenueopsApi = {
  dashboard: () => fetchJSON<RevenueOpsDashboard>('/dashboard'),
  listCampaigns: () => fetchJSON<CampaignRecord[]>('/campaigns'),
  listKeywords: () => fetchJSON<KeywordRecord[]>('/keywords'),
  listSearchTerms: () => fetchJSON<SearchTermRecord[]>('/search-terms'),
  listBudgets: () => fetchJSON<BudgetRecord[]>('/budgets'),
  listForecasts: () => fetchJSON<RevenueForecastRecord[]>('/forecasts'),
  listRevenuePlans: () => fetchJSON<RevenuePlanRecord[]>('/revenue-plans'),
  listOpportunities: () => fetchJSON<OpportunityRecord[]>('/opportunities'),
  listRecommendations: () => fetchJSON<RevenueRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<RevenueRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<{ id: string; mode: string; status: string }[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
